// Copyright 2026, University of Maryland, All Rights Reserved
/**
 * Configure which connected-source documents an agent accepts.
 *
 * cm-coding: Connectors declare which source record and document types they can
 * import; an agent chooses which of those documents it may analyze. Read the
 * available choices from the workspace's paired Vault and persist only the
 * agent's provider-scoped selections. Recreating connector types in this editor
 * could offer documents the connector never produces or bind agents to one
 * provider's configuration form.
 */
import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ChevronDown } from 'lucide-react'

import type { NodeApiClient } from '@/app-shell/services'
import { Button, Checkbox, Label } from '@/components/ui'
import { getWorkspaceVaultPairing } from '@/subsystems/vault/services/vault-api'
import { getConnectorDocumentMappingCatalog } from '@/subsystems/vault/connectors/api'
import { connectorDefinition } from '@/subsystems/vault/connectors/registry'

import type {
  AgentDocumentArtifactType,
  AgentDocumentMapping,
} from '../types'

interface Props {
  api: NodeApiClient
  orgId: string
  workspaceId: string
  value: AgentDocumentMapping[]
  disabled: boolean
  saving: boolean
  onSave: (value: AgentDocumentMapping[]) => void
}

const ARTIFACT_LABELS: Record<AgentDocumentArtifactType, string> = {
  attachment: 'Attachments',
  metadata: 'Metadata records',
}

const identity = (provider: string, recordType: string) => `${provider}\0${recordType}`

export function AgentDocumentMappingEditor({
  api,
  orgId,
  workspaceId,
  value,
  disabled,
  saving,
  onSave,
}: Props) {
  const [draft, setDraft] = useState(value)
  const [openProviders, setOpenProviders] = useState(
    () => new Set(value.map(mapping => mapping.provider)),
  )
  useEffect(() => setDraft(value), [value])

  const pairing = useQuery({
    queryKey: ['workspace-vault-pairing', orgId, workspaceId],
    queryFn: () => getWorkspaceVaultPairing(api, orgId, workspaceId),
    enabled: Boolean(orgId && workspaceId),
    staleTime: 5 * 60_000,
  })
  const catalog = useQuery({
    queryKey: ['connector-document-mapping', orgId, pairing.data?.vaultId],
    queryFn: () => getConnectorDocumentMappingCatalog(api, orgId, pairing.data!.vaultId),
    enabled: Boolean(pairing.data?.vaultId),
    staleTime: 60_000,
  })

  const draftByIdentity = useMemo(
    () => new Map(draft.map(mapping => [identity(mapping.provider, mapping.recordType), mapping])),
    [draft],
  )
  const dirty = JSON.stringify(draft) !== JSON.stringify(value)
  const sources = catalog.data?.sources ?? []
  const selectedSourceCount = new Set(draft.map(mapping => mapping.provider)).size

  const replace = (next: AgentDocumentMapping) => {
    setDraft(current => [
      ...current.filter(mapping => identity(mapping.provider, mapping.recordType) !== identity(next.provider, next.recordType)),
      next,
    ])
  }

  const toggleRecordType = (
    provider: string,
    recordType: string,
    artifactTypes: AgentDocumentArtifactType[],
  ) => {
    const key = identity(provider, recordType)
    setDraft(current => draftByIdentity.has(key)
      ? current.filter(mapping => identity(mapping.provider, mapping.recordType) !== key)
      : [...current, { provider, recordType, artifactTypes }])
  }

  const toggleArtifact = (
    mapping: AgentDocumentMapping,
    artifactType: AgentDocumentArtifactType,
  ) => {
    const artifactTypes = mapping.artifactTypes.includes(artifactType)
      ? mapping.artifactTypes.filter(value => value !== artifactType)
      : [...mapping.artifactTypes, artifactType]
    if (artifactTypes.length === 0) {
      setDraft(current => current.filter(item => identity(item.provider, item.recordType) !== identity(mapping.provider, mapping.recordType)))
      return
    }
    replace({ ...mapping, artifactTypes })
  }

  return (
    <section className="overflow-hidden rounded-[14px] border border-slate-200 bg-white shadow-[0_24px_60px_rgba(15,23,42,0.14)]">
      <div className="px-[22px] pt-[18px]">
        <h4 className="text-[15px] font-semibold text-slate-900">Document mapping</h4>
        <p className="text-sm text-muted-foreground">
          Choose what this agent processes. Available record types come from modules enabled in this workspace&apos;s connectors.
        </p>
      </div>

      {pairing.isPending || catalog.isPending ? (
        <p className="text-sm text-muted-foreground">Loading connected sources…</p>
      ) : !pairing.data || !catalog.data?.sources.length ? (
        <p className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
          Connect a source to this workspace Vault to configure document processing.
        </p>
      ) : (
        <div className="flex flex-col gap-2 px-[22px] pb-[18px] pt-4">
            {sources.map(source => {
              const definition = connectorDefinition(source.provider)
              const selectedCount = draft.filter(mapping => mapping.provider === source.provider).length
              const open = openProviders.has(source.provider)
              return (
                <div key={source.provider} className="overflow-hidden rounded-[10px] border border-slate-200">
                  <button
                    type="button"
                    aria-expanded={open}
                    className="flex w-full items-center gap-2.5 px-4 py-3 text-left hover:bg-[#fafbfc]"
                    onClick={() => setOpenProviders(current => {
                      const next = new Set(current)
                      if (next.has(source.provider)) next.delete(source.provider)
                      else next.add(source.provider)
                      return next
                    })}
                  >
                    <definition.SourceIcon className="h-[15px] w-[15px] shrink-0 text-slate-600" />
                    <span className="text-[13.5px] font-semibold text-slate-900">{definition.label}</span>
                    {selectedCount > 0 && (
                      <span className="inline-flex h-[17px] min-w-[17px] items-center justify-center rounded-full bg-slate-800 px-[5px] text-[11px] font-semibold text-white">
                        {selectedCount}
                      </span>
                    )}
                    <span className="ml-auto flex items-center gap-1.5">
                      <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
                      <span className="text-xs text-slate-400">Connected</span>
                    </span>
                    <ChevronDown className={`h-3.5 w-3.5 shrink-0 text-slate-400 transition-transform duration-150 ${open ? 'rotate-180' : ''}`} />
                  </button>
                  {open && (
                    <div className="flex flex-col gap-[7px] px-3 pb-3 pt-0.5">
                  {source.recordTypes.map(recordType => {
                    const key = identity(source.provider, recordType.recordType)
                    const selected = draftByIdentity.get(key)
                    return (
                      <div key={key} className="flex flex-wrap items-center gap-2.5 rounded-lg border border-[#eef1f5] bg-slate-50 px-3 py-2.5">
                        <Label className="flex cursor-pointer items-center gap-2.5 text-[13.5px] font-semibold text-slate-900">
                          <Checkbox
                            checked={Boolean(selected)}
                            className="h-4 w-4 rounded-[4.5px]"
                            disabled={disabled || saving}
                            onCheckedChange={() => toggleRecordType(
                              source.provider,
                              recordType.recordType,
                              recordType.artifactTypes,
                            )}
                          />
                          {recordType.recordType}
                        </Label>
                        <div className="ml-auto flex gap-[18px]">
                          {recordType.artifactTypes.map(artifactType => (
                            <Label key={artifactType} className={`flex items-center gap-[7px] text-[12.5px] font-normal text-slate-500 ${selected ? 'cursor-pointer' : 'cursor-default opacity-45'}`}>
                              <Checkbox
                                className="h-3.5 w-3.5 rounded"
                                checked={Boolean(selected?.artifactTypes.includes(artifactType))}
                                disabled={!selected || disabled || saving}
                                onCheckedChange={() => selected && toggleArtifact(selected, artifactType)}
                              />
                              {ARTIFACT_LABELS[artifactType]}
                            </Label>
                          ))}
                        </div>
                      </div>
                    )
                  })}
                    </div>
                  )}
                </div>
              )
            })}
        </div>
      )}

      {!disabled && !pairing.isPending && !catalog.isPending && Boolean(pairing.data && sources.length) && (
        <div className="flex items-center gap-3 border-t border-[#eef1f5] bg-[#fafbfc] px-[22px] py-[13px]">
          <span className="text-[12.5px] text-slate-500">
            {draft.length === 0
              ? 'Nothing selected yet'
              : `${draft.length} record type${draft.length === 1 ? '' : 's'} across ${selectedSourceCount} source${selectedSourceCount === 1 ? '' : 's'}`}
          </span>
          <Button className="ml-auto h-[34px] rounded-lg px-4 text-[13px]" disabled={!dirty || saving} onClick={() => onSave(draft)}>
            {saving ? 'Saving…' : 'Save document mapping'}
          </Button>
        </div>
      )}
    </section>
  )
}
